import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/shared/api/http/client';
import { ENDPOINTS } from '@/shared/api/endpoints';
import { toast } from '@/shared/ui/toastStore';
import type { SystemRoleSummary } from '@/features/policy/model/types';

export interface MessageRolePermission {
  messageRolePermissionId: string;
  senderRoleId: string;
  recipientRoleId: string;
  isActive: boolean;
}

const QUERY_KEY = ['chat', 'rolePermissions'] as const;

export async function fetchChatRolePermissions(): Promise<MessageRolePermission[]> {
  return apiClient.get<MessageRolePermission[]>(ENDPOINTS.CHAT_ROLE_PERMISSIONS);
}

export async function saveChatRolePermissionsForRole(senderRoleId: string, recipientRoleIds: string[]): Promise<void> {
  await apiClient.post(ENDPOINTS.CHAT_ROLE_PERMISSIONS_SAVE, { senderRoleId, recipientRoleIds });
}

/** Mevcut izinlerden rol → izinli roller haritası üretir. */
export function buildMatrix(perms: MessageRolePermission[]): Record<string, Set<string>> {
  const m: Record<string, Set<string>> = {};
  for (const p of perms) {
    if (!p.isActive) continue;
    if (!m[p.senderRoleId]) m[p.senderRoleId] = new Set();
    m[p.senderRoleId].add(p.recipientRoleId);
  }
  return m;
}

/**
 * Tüm roller için sohbet izin matrisini yükler ve kaydetme işlevi döner.
 * Yükleme durumu + hata yönetimi burada; bileşen yalnızca görünümü yönetir.
 */
export function useChatRolePermissionsManager(roles: SystemRoleSummary[]) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [perms, setPerms] = useState<MessageRolePermission[]>([]);
  const [matrix, setMatrix] = useState<Record<string, Set<string>>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    setLoading(true);
    fetchChatRolePermissions()
      .then((data) => {
        setPerms(data);
        setMatrix(buildMatrix(data));
      })
      .catch(() => toast.error(t('chat.rolePermissionsFailed')))
      .finally(() => setLoading(false));
  }, [t]);

  const toggle = (senderRoleId: string, recipientRoleId: string) => {
    setMatrix((prev) => {
      const cur = new Set(prev[senderRoleId] ?? []);
      if (cur.has(recipientRoleId)) cur.delete(recipientRoleId);
      else cur.add(recipientRoleId);
      return { ...prev, [senderRoleId]: cur };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await Promise.all(
        roles.map((r) =>
          saveChatRolePermissionsForRole(r.roleId, [...(matrix[r.roleId] ?? new Set())]),
        ),
      );
      void qc.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success(t('chat.rolePermissionsSaved'));
      return true;
    } catch {
      toast.error(t('chat.rolePermissionsFailed'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  return { perms, matrix, toggle, save, loading, saving };
}
